import { Logger, isBrowser } from '@hot-club-http/client/utils'
import { QueryClient } from '@tanstack/react-query'

import {
  SHARED_HOT_CLUB_HTTP_INSTANCES,
  SHARED_HOT_CLUB_QUERY_CLIENT,
  HTTP_STATUS_CODE,
  SHARED_HOT_CLUB_HTTP_GLOBAL_HEADERS,
  DEFAULT_STALE_TIME
} from './constants'
import type HotClubHttp from './hotClubHttp'
import type { ApiError, InitQueryClientParams } from './types'

/**
 * A utility class for managing global structures, shared HTTP instances, headers, and QueryClient,
 * for the HotClubHttp library. This class provides methods to manage instances and configurations globally.
 */
class HotClubHttpGlobals {
  static getGlobalInstances() {
    if (!window[SHARED_HOT_CLUB_HTTP_INSTANCES]) {
      this.initGlobalStructure()
    }

    return window[SHARED_HOT_CLUB_HTTP_INSTANCES]
  }
  static getGlobalHeaders() {
    if (!window[SHARED_HOT_CLUB_HTTP_GLOBAL_HEADERS]) {
      this.initGlobalStructure()
    }

    return window[SHARED_HOT_CLUB_HTTP_GLOBAL_HEADERS]
  }
  /**
   * Initializes the global structures required for the library, shared headers and HTTP instances.
   */
  static initGlobalStructure() {
    /** global headers */
    if (!window[SHARED_HOT_CLUB_HTTP_GLOBAL_HEADERS]) {
      window[SHARED_HOT_CLUB_HTTP_GLOBAL_HEADERS] = {}
    }
    /** instances */
    if (!window[SHARED_HOT_CLUB_HTTP_INSTANCES]) {
      window[SHARED_HOT_CLUB_HTTP_INSTANCES] = {}
    }
  }

  /**
   * Initializes or retrieves a global QueryClient instance for React Query.
   * @param debugMode - If true, logs the initialization of the QueryClient instance.
   * @returns {QueryClient} The shared QueryClient instance.
   */
  static initQueryClient(params?: InitQueryClientParams): QueryClient {
    const { debugMode } = params || { debugMode: false }
    let queryClientInstance: QueryClient = window[SHARED_HOT_CLUB_QUERY_CLIENT] || null

    if (!queryClientInstance) {
      window[SHARED_HOT_CLUB_QUERY_CLIENT] = new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: DEFAULT_STALE_TIME, // 30min
            refetchOnWindowFocus: false,
            retry: (failureCount, error) => {
              const maxRetries = 2
              const statusCode = (error as ApiError).statusCode

              return (
                statusCode < HTTP_STATUS_CODE.BAD_REQUEST ||
                (statusCode >= HTTP_STATUS_CODE.INTERNAL_SERVER_ERROR && failureCount <= maxRetries)
              )
            }
          }
        }
      })

      queryClientInstance = window[SHARED_HOT_CLUB_QUERY_CLIENT]

      debugMode &&
        Logger.info('initQueryClient - creating instance', {
          instance: queryClientInstance
        })
    }

    debugMode &&
      Logger.info('initQueryClient - using existing instance', {
        instance: queryClientInstance
      })

    return queryClientInstance
  }

  /**
   * Cancels all ongoing HTTP requests for all existing HotClubHttp instances.
   * @throws An error if cancellation fails.
   */
  static cancelAllRequests() {
    try {
      const clients = this.getGlobalInstances()
      const isValid = clients && Object.keys(clients).some((key) => clients[key])
      if (isValid) {
        Object.keys(clients).forEach((key) => {
          const { cancelRequestsManually } = clients[key]
          cancelRequestsManually?.()
        })
      }
    } catch (e) {
      throw new Error('[cancelAllRequests] ', e)
    }
  }

  /**
   * Removes specific headers from the global headers.
   * @param keys - A single header key or an array of header keys to remove.
   */
  static removeInstanceHeader(keys: string | string[]): void {
    try {
      const globalHeaders = this.getGlobalHeaders()
      const keysToRemove = Array.isArray(keys) ? keys : [keys]

      if (!keys || (Array.isArray(keys) && keys.length === 0)) {
        Logger.warn('removeInstanceHeader: No keys provided for removal')

        return
      }

      keysToRemove.forEach((key) => {
        if (key in globalHeaders) {
          if (typeof key !== 'string' || !(key in globalHeaders)) {
            return
          }

          delete globalHeaders[key]
        } else {
          Logger.warn(`removeInstanceHeader: Invalid or non-existent key: ${key}`)
        }
      })

      window[SHARED_HOT_CLUB_HTTP_GLOBAL_HEADERS] = globalHeaders
    } catch (e) {
      throw new Error('[removeInstanceHeader] ', e)
    }
  }

  /**
   * Updates default headers for all HotClubHttp instances.
   * @param headers - Headers to be added or updated across all instances.
   */
  static updateAllInstancesHeaders(headers: Record<string, string>): void {
    try {
      /** update global default headers */
      window[SHARED_HOT_CLUB_HTTP_GLOBAL_HEADERS] = {
        ...this.getGlobalHeaders(),
        ...headers
      }
    } catch (e) {
      throw new Error('[updateAllInstancesHeaders] ', e)
    }
  }

  /**
   * Retrieves an existing HotClubHttp instance by its key.
   * @param key - The identifier for the shared HotClubHttp instance.
   * @returns {HotClubHttp | null} The HotClubHttp instance, or null if not found.
   */
  static getInstanceByKey(key: string): HotClubHttp | null {
    try {
      if (!isBrowser()) return {} as HotClubHttp

      const instances = this.getGlobalInstances()
      if (instances && instances[key]) {
        return instances[key]
      }

      throw new Error(`instance not found for key: ${key}`)
    } catch (e) {
      // Logger.warn('getInstanceByKey', { message: e?.message })

      return null
    }
  }

  /**
   * Adds a new HotClubHttp instance by key.
   * @param key - The identifier for the new instance.
   * @param instance - The HotClubHttp instance to add.
   * @returns {HotClubHttp} The added HotClubHttp instance.
   * @throws An error if the key or instance is missing.
   */
  static addInstanceByKey(key: string, instance: HotClubHttp): HotClubHttp {
    try {
      if (!key || !instance)
        throw new Error(
          `[HotClubHttpGlobals][addInstanceByKey] Missing parameters, key:${key}, instance:${instance}`
        )

      const instances = this.getGlobalInstances()
      instances[key] = instance

      return instances[key]
    } catch (e) {
      throw new Error('[addInstanceByKey] ', e)
    }
  }
}

export default HotClubHttpGlobals
